import { TacItem } from '../../generalTypes';
import React, { useEffect, useState } from 'react';
import {
  getAllPartsThatAreAlsoAttachedAsProducts,
  getCommunicatedItemImageUrl,
  getCommunicatedItemName,
} from '../../state/products/productsHelpers';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import {
  KompisActionList,
  KompisActionListItem,
  KompisSheet,
  KompisSheetBody,
  KompisSheetBodyPadding,
  KompisSheetHeader,
} from '@inter-ikea-kompis/react-components';

import { ActionListControlEnum } from '@inter-ikea-kompis/component-action-list';

import {
  selectIsLandscape,
  selectIsMobile,
} from '../../state/userAgent/userAgentSelectors';
import { useSelector, useDispatch } from 'react-redux';
import styles from '../ConfMenu/ConfMenu.module.less';
import classNames from 'classnames';
import { SheetSizeEnum } from '@inter-ikea-kompis/component-sheet';
import { ADDITIONAL_INFO } from './AdditionalInfoSheet';
import { actionDequeueAllSheets } from '../../state/sheets/sheetActions';
import { thunkEnqueueSheet } from '../../state/sheets/sheetThunks';
import { translate } from '../../services/L10n';
import { t } from '../../translations';

export interface Props {
  item: TacItem;
}

export const COMBINED_PRODUCT_INFO = 'COMBINED_PRODUCT_INFO';

export const CombinedProductsSheet: React.FunctionComponent<Props> = ({
  item,
}) => {
  const dispatch = useDispatch();
  const isMobile = useSelector(selectIsMobile);
  const isLandscape = useSelector(selectIsLandscape);
  const attachedParts = getAllPartsThatAreAlsoAttachedAsProducts(item);
  const partsAsSheetsSelectedIndexMap = new Map();

  const [sheetVisible, setSheetVisible] = useState(true);

  const isRealProduct = (item: TacItem) => item.iows.length;

  useEffect(() => {
    attachedParts.forEach((part, index) => {
      if (isRealProduct(part))
        partsAsSheetsSelectedIndexMap.set(index, {
          sheetType: ADDITIONAL_INFO,
          item: part,
        });
    });

    return () => setSheetVisible(false);
  }, []);

  const onBodyClick = (event: Event) => event.stopPropagation();

  const itemSelected = ({ detail: { selectedIndex } }: any) => {
    const sheet = partsAsSheetsSelectedIndexMap.get(selectedIndex);
    dispatch(thunkEnqueueSheet(sheet));
  };

  const renderActionListItem = (item: TacItem, index: number) => {
    const communicatedImageUrl = getCommunicatedItemImageUrl(item);
    const communicatedItemName = getCommunicatedItemName(item);
    return (
      <KompisActionListItem
        theme={SkapaTheme}
        label={communicatedItemName}
        onSelect={itemSelected}
        key={item.id}
        selectedIndex={index}
        imageSrc={communicatedImageUrl}
        control={ActionListControlEnum.chevron}
      />
    );
  };

  const closeAllSheets = () => dispatch(actionDequeueAllSheets());

  const headingStyle = {
    fontSize: '24px',
    marginBottom: '4px',
  };

  const descriptionStyle = {
    fontSize: '14px',
    marginBottom: '16px',
  };

  return (
    <KompisSheet
      onOverlayClick={closeAllSheets}
      visible={sheetVisible}
      theme={SkapaTheme}
      sheetSize={SheetSizeEnum.small}
    >
      <KompisSheetHeader
        floatingHeader={isMobile && isLandscape}
        theme={SkapaTheme}
        heading={''}
        onCloseButtonClick={closeAllSheets}
      />
      <KompisSheetBody
        theme={SkapaTheme}
        verticalScroll={false}
        className={classNames(styles.KompisSheetBody)}
        // @ts-ignore KompisSheetBody does not support click events
        onClick={onBodyClick}
      >
        <KompisSheetBodyPadding theme={SkapaTheme}>
          <h1 style={headingStyle}>{`${translate(t.PRODUCT_INFO_SHEET)}`}</h1>
          <p style={descriptionStyle}>{`${translate(
            t.PRODUCT_INFO_SHEET_DESCRIPTION
          )}`}</p>
          <KompisActionList>
            {attachedParts.map((part, index) =>
              isRealProduct(part) ? renderActionListItem(part, index) : null
            )}
          </KompisActionList>
        </KompisSheetBodyPadding>
      </KompisSheetBody>
    </KompisSheet>
  );
};
